import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft, Plus, Send, Sparkles, Trash2, MessageSquare, Loader2, Lock, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STARTERS = [
  'Which careers suit someone who likes biology and helping people?',
  'What should I pick after Grade 10 if I enjoy maths?',
  'How is product design different from graphic design?',
  'What skills matter most for a career in data?',
];

/** Minimal renderer: paragraphs, **bold** and markdown links to career pages. */
function Rendered({ text }) {
  const navigate = useNavigate();
  const blocks = text.split(/\n{2,}/);
  const inline = (chunk, key) => {
    const parts = [];
    const re = /\[([^\]]+)\]\((\/careers\/[a-z0-9-]+)\)|\[(\/careers\/[a-z0-9-]+)\]|(?<![([/\w])(\/careers\/[a-z0-9-]+)|\*\*([^*]+)\*\*/g;
    const pretty = (slug) => slug.split('/').pop().split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    let last = 0; let m; let i = 0;
    while ((m = re.exec(chunk)) !== null) {
      if (m.index > last) parts.push(chunk.slice(last, m.index));
      const bareSlug = m[3] || m[4];
      if (m[1] || bareSlug) {
        const href = m[2] || bareSlug;
        parts.push(
          <button
            key={`${key}-l${i}`}
            onClick={() => navigate(href)}
            className="text-[#F97D6A] font-medium hover:underline"
            data-testid="buddy-career-link"
          >
            {m[1] || pretty(bareSlug)}
          </button>,
        );
      } else {
        parts.push(<strong key={`${key}-b${i}`} className="font-semibold">{m[5]}</strong>);
      }
      last = m.index + m[0].length;
      i += 1;
    }
    if (last < chunk.length) parts.push(chunk.slice(last));
    return parts;
  };
  return (
    <>
      {blocks.map((b, i) => (
        <p key={i} className={i ? 'mt-3' : ''}>{inline(b, i)}</p>
      ))}
    </>
  );
}

function AcknowledgmentCard({ notice, onAccept, busy }) {
  const [agreed, setAgreed] = useState(false);
  return (
    <div className="max-w-xl mx-auto bg-white rounded-2xl border border-gray-200 p-7" data-testid="buddy-ack-card">
      <div className="w-11 h-11 rounded-full bg-[#FFF0ED] flex items-center justify-center mb-4">
        <Sparkles className="h-5 w-5 text-[#F97D6A]" />
      </div>
      <h2 className="text-xl font-bold text-[#1F2937] font-['Outfit']">{notice?.title || 'Before you start'}</h2>
      <ul className="mt-4 space-y-2.5">
        {(notice?.points || []).map((p, i) => (
          <li key={i} className="flex gap-2.5 text-sm text-[#4B5563] leading-relaxed">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#F97D6A] shrink-0" />
            {p}
          </li>
        ))}
      </ul>
      <label className="flex items-start gap-2.5 mt-5 bg-[#F9FAFB] border border-gray-200 rounded-xl p-3 cursor-pointer">
        <Checkbox checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" data-testid="buddy-ack-checkbox" />
        <span className="text-sm text-[#1F2937] leading-relaxed">{notice?.confirm}</span>
      </label>
      <Button
        onClick={onAccept}
        disabled={!agreed || busy}
        className="w-full mt-4 bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 font-semibold"
        data-testid="buddy-ack-accept-btn"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start using My Career Buddy AI'}
      </Button>
    </div>
  );
}

export default function CareerBuddyAIPage() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const [status, setStatus] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [pendingReply, setPendingReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [childName, setChildName] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const [st, ch] = await Promise.all([
        api.get(`/buddy-ai/${childId}/status`),
        api.get(`/children/${childId}`).catch(() => ({ data: {} })),
      ]);
      setStatus(st.data);
      setChildName(ch.data?.full_name || '');
      if (st.data.enabled && st.data.acknowledged) {
        const res = await api.get(`/buddy-ai/${childId}/conversations`);
        setConversations(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open My Career Buddy AI');
      navigate(`/parent/children/${childId}`);
    }
  }, [childId, navigate]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // switching to another child must not leave the previous child's chat on screen
  useEffect(() => {
    setActiveId('');
    setMessages([]);
    setDraft('');
    setPendingReply('');
    setConversations([]);
    setStatus(null);
  }, [childId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, pendingReply]);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/buddy-ai/${childId}/acknowledge`, { confirmed: true });
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save that');
    }
    setBusy(false);
  };

  const openConversation = async (id) => {
    setActiveId(id);
    setMessages([]);
    try {
      const res = await api.get(`/buddy-ai/conversations/${id}`, { params: { child_id: childId } });
      setMessages(res.data.messages);
    } catch {
      toast.error('Could not open that conversation');
    }
  };

  const newChat = () => {
    setActiveId('');
    setMessages([]);
    setDraft('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const removeConversation = async (id) => {
    try {
      await api.delete(`/buddy-ai/conversations/${id}`, { params: { child_id: childId } });
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeId === id) newChat();
      toast.success('Conversation deleted');
    } catch {
      toast.error('Could not delete that conversation');
    }
    setConfirmDelete(null);
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? draft).trim();
    if (!text || streaming) return;
    setDraft('');
    setStreaming(true);
    setPendingReply('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);

    let conversationId = activeId;
    try {
      if (!conversationId) {
        const created = await api.post(`/buddy-ai/${childId}/conversations`);
        conversationId = created.data.id;
        setActiveId(conversationId);
      }
      const res = await fetch(`${API}/buddy-ai/conversations/${conversationId}/message?child_id=${childId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (!res.ok || !res.body) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || 'The assistant could not answer just now');
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let answer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        for (const frame of frames) {
          if (!frame.startsWith('data: ')) continue;
          const payload = JSON.parse(frame.slice(6));
          if (payload.delta) {
            answer += payload.delta;
            setPendingReply(answer);
          } else if (payload.error) {
            toast.error(payload.error);
          }
        }
      }
      if (answer) setMessages((prev) => [...prev, { role: 'assistant', content: answer }]);
      setPendingReply('');
      setStreaming(false);
      const [list, st] = await Promise.all([
        api.get(`/buddy-ai/${childId}/conversations`),
        api.get(`/buddy-ai/${childId}/status`),
      ]);
      setConversations(list.data);
      setStatus(st.data);
    } catch (err) {
      setMessages((prev) => prev.slice(0, -1));
      setDraft(text);
      toast.error(err.message || 'Could not send that message');
    }
    setStreaming(false);
  };

  if (!status) {
    return <div className="py-20 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-300" /></div>;
  }

  const backLink = (
    <Button variant="ghost" onClick={() => navigate(`/parent/children/${childId}`)}
            className="text-sm text-[#6B7280] hover:text-[#1F2937] mb-4 -ml-2 rounded-lg"
            data-testid="buddy-back-btn">
      <ArrowLeft className="h-4 w-4 mr-1" /> Back to {childName || 'child'}
    </Button>
  );

  if (!status.enabled) {
    return (
      <div data-testid="buddy-disabled">
        {backLink}
        <div className="max-w-md mx-auto bg-white rounded-2xl border border-gray-200 p-8 text-center mt-6">
          <Lock className="h-6 w-6 text-gray-300 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-[#1F2937] font-['Outfit']">Not switched on yet</h2>
          <p className="text-sm text-[#6B7280] mt-2 leading-relaxed">
            My Career Buddy AI is not available for your school at the moment. Your school can ask
            CareersPOV to enable it.
          </p>
        </div>
      </div>
    );
  }

  if (!status.acknowledged) {
    return (
      <div data-testid="buddy-ack-page">
        {backLink}
        <AcknowledgmentCard notice={status.acknowledgment} onAccept={accept} busy={busy} />
      </div>
    );
  }

  const usage = status.usage || {};
  const outOfMessages = usage.used_today >= usage.daily_limit;

  return (
    <div data-testid="career-buddy-ai-page">
      {backLink}
      <div className="grid lg:grid-cols-[260px_1fr] gap-4 lg:h-[calc(100vh-190px)] lg:min-h-[520px]">
        {/* Conversations */}
        <aside className="bg-white rounded-2xl border border-gray-200 flex flex-col overflow-hidden max-h-[40vh] lg:max-h-none">
          <div className="p-3 border-b border-gray-100">
            <Button onClick={newChat}
                    className="w-full bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-10 text-sm font-semibold gap-1.5"
                    data-testid="buddy-new-chat-btn">
              <Plus className="h-4 w-4" /> New chat
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="buddy-conversation-list">
            {conversations.length === 0 && (
              <p className="text-xs text-gray-400 px-2 py-4 text-center">No saved conversations yet.</p>
            )}
            {conversations.map((c) => (
              <div key={c.id}
                   className={`group flex items-center gap-1 rounded-xl px-2.5 py-2 cursor-pointer transition-colors ${
                     activeId === c.id ? 'bg-[#FFF0ED]' : 'hover:bg-gray-50'}`}
                   onClick={() => openConversation(c.id)}
                   data-testid={`buddy-conversation-${c.id}`}>
                <MessageSquare className={`h-3.5 w-3.5 shrink-0 ${activeId === c.id ? 'text-[#F97D6A]' : 'text-gray-300'}`} />
                <span className="flex-1 text-[13px] text-[#1F2937] truncate">{c.title}</span>
                <button onClick={(e) => { e.stopPropagation(); setConfirmDelete(c); }}
                        className="text-gray-300 hover:text-red-500 transition-opacity lg:opacity-0 lg:group-hover:opacity-100"
                        data-testid={`buddy-delete-${c.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="px-3 py-2.5 border-t border-gray-100">
            <p className="text-[11px] text-gray-400" data-testid="buddy-usage">
              {usage.used_today || 0} of {usage.daily_limit} questions used today
            </p>
          </div>
        </aside>

        {/* Conversation */}
        <section className="bg-white rounded-2xl border border-gray-200 flex flex-col overflow-hidden h-[65vh] lg:h-auto">
          <div className="flex items-center gap-2.5 px-5 py-3 border-b border-gray-100">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F97D6A] to-[#E85D4A] flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-[#1F2937] font-['Outfit']">My Career Buddy AI</h1>
              <p className="text-[11px] text-[#9CA3AF]">Careers, subjects and study paths · AI assistant</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5" data-testid="buddy-messages">
            {messages.length === 0 && !pendingReply && (
              <div className="max-w-lg mx-auto text-center py-8" data-testid="buddy-empty-state">
                <h2 className="text-lg font-bold text-[#1F2937] font-['Outfit']">
                  What would you like to explore?
                </h2>
                <p className="text-sm text-[#6B7280] mt-1.5">
                  Ask about careers, subject choices or what to study next. Answers use your school's
                  career library where it covers the topic.
                </p>
                <div className="grid sm:grid-cols-2 gap-2 mt-5 text-left">
                  {STARTERS.map((s, i) => (
                    <button key={s} onClick={() => send(s)}
                            className="rounded-xl border border-gray-200 px-3.5 py-2.5 text-[13px] text-[#374151] hover:border-[#F97D6A]/50 hover:bg-[#FFF8F6] transition-colors"
                            data-testid={`buddy-starter-${i}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              m.role === 'user' ? (
                <div key={i} className="flex justify-end mb-4">
                  <div className="bg-[#F97D6A] text-white rounded-2xl rounded-tr-md px-4 py-2.5 max-w-[80%] text-sm leading-relaxed"
                       data-testid="buddy-user-message">
                    {m.content}
                  </div>
                </div>
              ) : (
                <div key={i} className="flex gap-2.5 mb-5">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#F97D6A] to-[#E85D4A] flex items-center justify-center shrink-0 mt-0.5">
                    <Sparkles className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div className="text-sm text-[#374151] leading-relaxed max-w-[85%]" data-testid="buddy-assistant-message">
                    <Rendered text={m.content} />
                  </div>
                </div>
              )
            ))}

            {pendingReply && (
              <div className="flex gap-2.5 mb-5">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#F97D6A] to-[#E85D4A] flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="h-3.5 w-3.5 text-white" />
                </div>
                <div className="text-sm text-[#374151] leading-relaxed max-w-[85%]" data-testid="buddy-streaming-message">
                  <Rendered text={pendingReply} />
                </div>
              </div>
            )}

            {streaming && !pendingReply && (
              <div className="flex gap-2.5 mb-5 items-center" data-testid="buddy-thinking">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#F97D6A] to-[#E85D4A] flex items-center justify-center shrink-0">
                  <Sparkles className="h-3.5 w-3.5 text-white animate-pulse" />
                </div>
                <span className="text-xs text-[#9CA3AF]">Thinking…</span>
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 p-3">
            {outOfMessages ? (
              <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-xl px-3 py-2.5"
                   data-testid="buddy-limit-notice">
                <ShieldAlert className="h-4 w-4 shrink-0" />
                You have used today's {usage.daily_limit} questions for this child. Please carry on tomorrow.
              </div>
            ) : (
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value.slice(0, 800))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                  }}
                  rows={1}
                  placeholder="Ask about a career or subject choice…"
                  className="flex-1 resize-none rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-[#1F2937] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#F97D6A]/40 focus:border-[#F97D6A] max-h-32"
                  data-testid="buddy-input"
                />
                <Button onClick={() => send()} disabled={!draft.trim() || streaming}
                        className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-10 w-10 p-0 shrink-0"
                        data-testid="buddy-send-btn">
                  {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            )}
            <p className="text-[10px] text-gray-400 mt-2 px-1">
              AI assistant — check admissions and eligibility details on official sources.
            </p>
          </div>
        </section>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="buddy-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-['Outfit']">Delete this conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.title}" and its messages will be removed for good.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={() => removeConversation(confirmDelete.id)}
                               className="rounded-xl bg-red-500 hover:bg-red-600"
                               data-testid="buddy-delete-confirm-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
